import React, { useRef, useState } from 'react';
import { Loader2, Upload, X, Check } from 'lucide-react';
import { api, fileUrl } from '../lib/api';
import { downscaleToDataUrl } from '../lib/img';
import { toast } from '../hooks/use-toast';

// Embeddable minimal person creator: name + 1-4 face reference photos.
// Exists so a recipe that needs a face never dead-ends into "go to
// /app/influencers, create one, come back and start over". Voice, persona,
// style, aspect and the rest deliberately do NOT appear here -- the backend's
// InfluencerIn defaults every one of them, and the full editor in
// Influencers.jsx is where that tuning belongs. This is the fast lane.
const MAX_REFS = 4;

export default function QuickPersonCreate({ onCreated, onCancel }) {
  const fileRef = useRef(null);
  const [name, setName] = useState('');
  const [refs, setRefs] = useState([]); // [{id,url}]
  const [uploading, setUploading] = useState(false);
  const [creating, setCreating] = useState(false);

  const uploadFaces = async (fileList) => {
    const files = Array.from(fileList || []).slice(0, MAX_REFS - refs.length);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        // Same path the full editor uses: downscale client-side, store as an
        // 'influencer' asset so it lands in the same library bucket.
        const dataUrl = await downscaleToDataUrl(f, 1280);
        const a = await api.createAsset({
          type: 'influencer', name: f.name.replace(/\.[^.]+$/, '').slice(0, 60), dataUrl,
        });
        const url = a.url || (a.file_key ? `/api/files/${a.file_key}` : null);
        setRefs((prev) => [...prev, { id: a.id, url }]);
      }
    } catch (e) {
      toast({ title: 'Could not upload that photo', description: e.message });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const create = async () => {
    if (!name.trim()) { toast({ title: 'Give this person a name' }); return; }
    if (refs.length === 0) { toast({ title: 'Add at least one photo of their face' }); return; }
    setCreating(true);
    try {
      // Everything not sent here falls back to the backend's InfluencerIn
      // defaults (voice nova, English, 9:16...). First photo is the cover.
      const infl = await api.createInfluencer({
        name: name.trim(),
        reference_asset_ids: refs.map((r) => r.id),
        cover_asset_id: refs[0].id,
      });
      onCreated?.(infl);
    } catch (e) {
      if (e.status === 402 || /limit/i.test(e.message || '')) {
        toast({ title: 'Person limit reached', description: 'Unlock unlimited people with Pro Unlimited (OTO1).' });
      } else {
        toast({ title: 'Could not save this person', description: e.message });
      }
      setCreating(false);
    }
  };

  return (
    <div className="rounded-lg border border-[#2A2A30] bg-[#141417] p-3"
         data-testid="quick-person-create">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium text-node-model">New person</p>
        {onCancel && (
          <button onClick={onCancel} className="text-white/35 hover:text-white"
                  data-testid="quick-person-cancel">
            <X size={13} />
          </button>
        )}
      </div>

      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Name - e.g. Marta, Me, Our founder"
        data-testid="quick-person-name"
        className="mt-2 w-full rounded-md border border-[#2A2A30] bg-[#0E0E10] px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-white/40 focus:outline-none"
      />

      <div className="mt-2 flex flex-wrap gap-2">
        {refs.map((r) => (
          <div key={r.id} className="h-14 w-14 overflow-hidden rounded-lg border border-[#2A2A30]">
            {r.url
              ? <img src={fileUrl(r.url)} alt="" className="h-full w-full object-cover" />
              : null}
          </div>
        ))}
        {refs.length < MAX_REFS && (
          <button onClick={() => fileRef.current?.click()} disabled={uploading}
                  data-testid="quick-person-upload"
                  className="flex h-14 w-14 flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-[#2A2A30] text-white/40 hover:border-white/40 hover:text-white disabled:opacity-50">
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            <span className="text-[9px]">Photo</span>
          </button>
        )}
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
               onChange={(e) => uploadFaces(e.target.files)} />
      </div>
      <p className="mt-1.5 text-[11px] text-white/30">
        1-4 clear photos of the same face. These keep the face consistent in every session.
      </p>

      <button onClick={create} disabled={creating || uploading || !name.trim() || refs.length === 0}
              data-testid="quick-person-save"
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg bg-white py-2 text-xs font-medium text-black hover:bg-white/90 disabled:opacity-40">
        {creating ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
        Save person
      </button>
    </div>
  );
}
